import React, { useContext, useEffect, useMemo } from 'react';
import { CustomerAccountNavigation, useAccountCrumbs, } from '@thd-olt-component-react/customer-account-navigation';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Alert } from '@one-thd/sui-atomic-components';
import { Route, ScrollToTop, Routes } from '@thd-olt-component-react/router';
import { AppContext } from '@thd-nucleus/app-render';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { arrayOf, extend, params, QueryProvider, shape, useDataModel, string as stringType, number as numberType, } from '@thd-nucleus/data-sources';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { ModalProvider } from './context/modal-context';
import { useInterceptors } from './hooks/use-interceptors';
import { Error } from './pages';
import { getRoutes } from './routes.js';
import { DOCUMENT_TITLE } from './constants/general';
import { PageTitle } from './components/shared/page-title/page-title';
import { generateBreadcrumbs, mapCrumbsToStaticCrumbs, } from './utils/breadcrumbs';
import { generatePageTitle } from './utils/pageTitle';
export var CheckProAllowance = function (_a) {
    var isAdmin = _a.isAdmin, isLoggedInAsB2C = _a.isLoggedInAsB2C, userId = _a.userId, svocId = _a.svocId, channelId = _a.channelId, children = _a.children;
    var _b = useDataModel('loyaltyMembershipDetails', {
        variables: { userId: userId, customerAccountId: svocId, channelId: channelId },
        ssr: false,
        skip: !userId || !svocId,
        context: { withAuth: true },
    }), data = _b.data, loading = _b.loading, called = _b.called;
    var isPartOfRunnerAllowance = useMemo(function () {
        var _a, _b, _c;
        if (!data)
            return false;
        return !!((_c = (_b = (_a = data.loyaltyMembershipDetails) === null || _a === void 0 ? void 0 : _a.proTiers) === null || _b === void 0 ? void 0 : _b.benefitTags) === null || _c === void 0 ? void 0 : _c.find(function (_a) {
            var tagName = _a.tagName;
            return tagName === 'runnerAllowance';
        }));
    }, [data]);
    useEffect(function () {
        if ((called && !loading && !isPartOfRunnerAllowance) ||
            isLoggedInAsB2C ||
            !isAdmin) {
            window.location.replace('/');
        }
    }, [isPartOfRunnerAllowance, isLoggedInAsB2C, isAdmin, loading, called]);
    if (isLoggedInAsB2C || !isAdmin || !isPartOfRunnerAllowance)
        return null;
    return React.createElement(React.Fragment, null, children);
};
var AppRoutes = function (_a) {
    var customerType = _a.customerType, userId = _a.userId, svocId = _a.svocId, customerRole = _a.customerRole, channelId = _a.channelId;
    var instance = useContext(AppContext).instance;
    var isConsumerApp = useContext(ExperienceContext).isConsumerApp;
    var redirectError = useInterceptors().redirectError;
    var _b = instance.router, currentRouteName = _b.currentRouteName, currentRouteComponent = _b.currentRouteComponent;
    var routes = getRoutes(currentRouteName, currentRouteComponent);
    var isRouteError = !currentRouteName;
    var isAdmin = customerRole.toLocaleUpperCase() === 'ADMIN';
    var isLoggedInAsB2C = useMemo(function () {
        var isB2C = customerType.toLocaleUpperCase() === 'B2C';
        return !!(svocId && userId && isB2C);
    }, [svocId, userId, customerType]);
    var pageTitle = generatePageTitle(currentRouteName);
    var crumbs = useAccountCrumbs(generateBreadcrumbs(currentRouteName));
    var staticCrumbs = mapCrumbsToStaticCrumbs(crumbs);
    useEffect(function () {
        document.title = DOCUMENT_TITLE;
    }, []);
    if (isRouteError) {
        return React.createElement(Error, null);
    }
    return (React.createElement("div", { className: "sui-m-auto sui-p-[10px] sui-max-w-[1440px]" }, redirectError ? (React.createElement("div", { className: "sui-pt-8" },
        React.createElement(Alert, { status: "info" }, redirectError.message))) : (React.createElement(React.Fragment, null,
        React.createElement(ScrollToTop, null),
        React.createElement("div", { className: "sui-grid sm:sui-py-4" },
            !isConsumerApp && (React.createElement(ErrorBoundary, { name: "breadcrumbs" },
                React.createElement(Breadcrumbs, { staticCrumbs: staticCrumbs }))),
            React.createElement(PageTitle, { title: pageTitle })),
        React.createElement("div", { className: "sui-grid sui-grid-cols-12 sui-gap-5" },
            React.createElement("div", { className: "sui-col-span-12 lg:sui-col-span-3" },
                React.createElement(ErrorBoundary, { name: "customer-account-navigation" },
                    React.createElement(CustomerAccountNavigation, null))),
            React.createElement("div", { className: "sui-col-span-12 lg:sui-col-span-9" },
                React.createElement("div", { className: "sui-pt-4" },
                    React.createElement(Routes, null,
                        React.createElement(Route, { exact: true, path: routes.pojobnames.path, element: React.createElement(routes.pojobnames.component, { customerType: customerType, customerRole: customerRole, userId: userId, svocId: svocId }) }),
                        React.createElement(Route, { exact: true, path: routes.proallowance.path, element: React.createElement(CheckProAllowance, { isAdmin: isAdmin, isLoggedInAsB2C: isLoggedInAsB2C, channelId: channelId, userId: userId, svocId: svocId },
                                React.createElement(routes.proallowance.component, null)) }),
                        React.createElement(Route, { exact: true, path: routes.assignproallowance.path, element: React.createElement(CheckProAllowance, { isAdmin: isAdmin, isLoggedInAsB2C: isLoggedInAsB2C, channelId: channelId, userId: userId, svocId: svocId },
                                React.createElement(routes.assignproallowance.component, null)) })))))))));
};
AppRoutes.dataModel = extend({
    loyaltyMembershipDetails: params({
        userId: stringType().isRequired(),
        customerAccountId: stringType().isRequired(),
        channelId: numberType().isRequired(),
    }).shape({
        proTiers: shape({
            benefitTags: arrayOf(shape({
                tagName: stringType(),
                value: stringType(),
            })),
        }),
    }),
}, CustomerAccountNavigation, Breadcrumbs);
export var App = function (_a) {
    var customerType = _a.customerType, customerRole = _a.customerRole, userId = _a.userId, svocId = _a.svocId, channelId = _a.channelId;
    return (React.createElement(ModalProvider, null,
        React.createElement(QueryProvider, { cacheKey: "app-routes", defaultVariables: {
                channelId: 1,
                userId: userId,
                customerAccountId: svocId,
            } },
            React.createElement(AppRoutes, { customerType: customerType, customerRole: customerRole, userId: userId, svocId: svocId, channelId: channelId }))));
};
App.propTypes = {};
App.defaultProps = {};
